﻿import {JSX, SyntheticEvent, useCallback} from "react";
import {useControlId} from "../helpers/useControlId";
import Switch, {SwitchLayout} from "../HtmlControls/Switch";
import {useMvvmFieldSubscription} from "./MvvmDataProvider";
import {MvvmControlProps} from "./MvvmControlProps";

import {useRegisterValidation} from "./useRegisterValidation";
import {unwrapMvvmContext} from "./useMvvmTracking";

// A switch is a boolean — it has no error state, so the error/hint-related props are omitted.
export interface MvvmSwitchProps<T extends object = never> extends Omit<MvvmControlProps<T, boolean>, "hint" | "inlineError" | "placeholder" | "autoFocus"> {
	layout?: SwitchLayout;
}

export default function MvvmSwitch<T extends object = never>(props: MvvmSwitchProps<T>): JSX.Element {
	const {
		context: contextProp,
		property,
		id,
		label,
		column,
		hidden,
		disabled,
		validation,
		onChange,
		onBlur,
		layout
	} = props;

	// Controls read the raw context — their re-renders come from their own field
	// subscription, not from recording reads onto a host facade.
	const context = unwrapMvvmContext(contextProp);
	const controlId = useControlId(id);

	const checked = context.getValue<boolean>(property) ?? false;

	const onChangeInternal = useCallback(function (e: SyntheticEvent<HTMLInputElement>): void {
		const value = e.currentTarget.checked;
		onChange?.(value);
		context.setValue(property, value);
	}, [context, property, onChange]);

	useRegisterValidation(context, property, controlId, hidden, validation);
	useMvvmFieldSubscription(context, property, {subscribeError: false});

	return <Switch id={id}
	               label={label}
	               name={controlId}
	               column={column}
	               onChange={onChangeInternal}
	               onBlur={onBlur}
	               hidden={hidden}
	               disabled={disabled}
	               checked={checked}
	               layout={layout}/>;
};
